import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdList, MdSearch, MdEdit, MdDelete, MdFileDownload, MdPictureAsPdf, MdClose, MdSave,
  MdUpload, MdPrint, MdInfo, MdDescription, MdSwapHoriz,
  MdUnfoldMore, MdUnfoldLess, MdAdd,
} from 'react-icons/md';
import api from '../api/axios';
import IdentifierBadges from '../components/IdentifierBadges';
import { useChangeStream } from '../lib/useChangeStream';
import { useAuth } from '../context/AuthContext';
import { downloadFromApi, parseBlobError } from '../lib/fileDownload';
import { tsFilename } from '../lib/filename';
import SearchableSelect from '../components/SearchableSelect';
import ImportProgress from '../components/ImportProgress';
import ImportErrorReport from '../components/ImportErrorReport';
import { useAssetImport, importErrorMessage } from '../lib/useAssetImport';

const PAGE_SIZE = 20;

/**
 * Compact paginator window. With 11k+ assets the previous "render every page
 * number" approach produced 600+ buttons that wrapped 3 rows deep and pushed
 * the table off-screen. This returns the canonical first/last + neighbours +
 * ellipses pattern: « 1 … 37 38 39 … 61 ».
 */
function compactPagerWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out = [1];
  const start = Math.max(2, current - 1);
  const end   = Math.min(total - 1, current + 1);
  if (start > 2) out.push('…');
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push('…');
  out.push(total);
  return out;
}

const Toast = ({ msg, type, onClose }) => (
  // Defensive sizing + fallback text so a future blank-msg bug at least
  // tells the user where to look. minWidth + padding ensures the box is
  // visible even for short messages; max-width keeps long error text
  // from running off the right edge.
  <div className={`toast toast-${type}`} style={{
    position: 'fixed', top: 20, right: 20, zIndex: 9999,
    minWidth: 280, maxWidth: 480, padding: '12px 16px',
    display: 'flex', alignItems: 'flex-start', gap: 12,
    fontSize: 14, lineHeight: 1.4,
  }}>
    <div style={{ flex: 1 }}>{msg || '(no message — see browser console)'}</div>
    <button onClick={onClose} style={{
      background: 'none', border: 'none', color: 'inherit',
      cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0,
    }} aria-label="Dismiss">×</button>
  </div>
);

// Styled confirm modal — replaces window.confirm() for any in-app
// confirmation. Native confirm() is blocked by some browsers, looks
// alien, and can't be styled. This component renders a dimmed overlay
// + centered card with two buttons. Resolves the returned promise to
// true (OK) or false (Cancel / dismiss).
const ConfirmModal = ({
  open, title, message, confirmLabel = 'OK', cancelLabel = 'Cancel',
  confirmStyle = 'primary', onConfirm, onCancel, onDismiss,
}) => {
  if (!open) return null;
  const confirmBg = confirmStyle === 'danger' ? 'var(--danger, #d32f2f)' : 'var(--primary, #1a3c6e)';
  // When the caller distinguishes "overlay/X click" from the Cancel
  // button (e.g. import-confirm where Cancel = "Skip duplicates" is a
  // real action and clicking outside means "don't import at all"), they
  // can pass an explicit onDismiss. If not provided, fall back to
  // onCancel — the legacy behaviour matches every other modal we ship.
  const dismiss = onDismiss || onCancel;
  return (
    <div
      onClick={dismiss}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9998, padding: 20,
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: 14, maxWidth: 460, width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
      }}>
        <div style={{ padding: '18px 22px 6px', borderBottom: '1px solid #e0e6ef', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 17, color: '#1a3c6e' }}>{title}</h3>
          <button onClick={dismiss} aria-label="Close" style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#90a4ae', fontSize: 22, lineHeight: 1, padding: 0,
          }}>×</button>
        </div>
        <div style={{ padding: '14px 22px 18px', fontSize: 14, color: '#37474f', lineHeight: 1.55, whiteSpace: 'pre-line' }}>
          {message}
        </div>
        <div style={{ padding: '12px 18px', background: '#f8fafc', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{
            padding: '8px 18px', borderRadius: 8, border: '1px solid #cbd5e1',
            background: 'white', color: '#37474f', cursor: 'pointer', fontSize: 14, fontWeight: 500,
          }}>{cancelLabel}</button>
          <button onClick={onConfirm} style={{
            padding: '8px 18px', borderRadius: 8, border: 'none',
            background: confirmBg, color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600,
          }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

const AllAssets = () => {
  const nav = useNavigate();
  const { hasPermission } = useAuth();
  const can = {
    exportExcel: hasPermission('export-excel'),
    exportPdf:   hasPermission('export-pdf'),
    importData:  hasPermission('import-data'),
    delete:      hasPermission('delete-asset'),
    bulkDelete:  hasPermission('bulk-delete'),
    create:      hasPermission('asset-registration'),
  };
  const [assets, setAssets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState(['active', 'inactive', 'maintenance', 'retired', 'lost', 'disposed']);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [selectingAll, setSelectingAll] = useState(false);
  // Typing fires one request per keystroke otherwise, and on a large company
  // each is real work on a shared server.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const reqSeq = useRef(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editModal, setEditModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [importResult, setImportResult] = useState(null);
  // Per-row expand state for the inline "expand-row" feature. compactMode
  // is the global default ("compact" hides everything except #/Name/Location/
  // Status); expandedIds carries per-row overrides (rows toggled away from
  // the default). When the user flips the global toggle we clear overrides
  // so the new mode is consistent across the list.
  const [compactMode, setCompactMode] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const isRowExpanded = (id) => {
    const overridden = expandedIds.has(id);
    return compactMode ? overridden : !overridden;
  };
  const toggleRow = (id) => {
    const next = new Set(expandedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpandedIds(next);
  };
  const [bulkMoveModal, setBulkMoveModal] = useState(false);
  const [bulkMove, setBulkMove] = useState({ to_location_id: '', reason_id: '', notes: '' });
  const [reasons, setReasons] = useState([]);
  const [toast, setToast] = useState(null);
  // Pending file upload — held while we ask the user whether to update or
  // skip duplicates via the styled ConfirmModal. Replaces window.confirm()
  // which the browser styled differently (and blocked outright on some
  // multi-tab kiosks).
  const [pendingImport, setPendingImport] = useState(null);
  // Generic confirm modal: { title, message, confirmLabel, confirmStyle, onConfirm }.
  // Setting null hides it. Used for bulk-delete prompts.
  const [confirmModal, setConfirmModal] = useState(null);
  const importRef = useRef();
  // Excel-import progress + all-or-nothing failure report. Shared with
  // AssetRegistration — the upload plumbing lives in lib/useAssetImport.js so
  // there is exactly one implementation of it.
  const {
    phase: importPhase,
    percent: importPercent,
    fileName: importFileName,
    importing,
    report: importErrors,
    clearReport: clearImportErrors,
    runImport: uploadImport,
  } = useAssetImport();

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Server-side pagination. This page used to pull EVERY asset and slice it
  // in the browser. When one company reached 145,290 assets that response was
  // 231 MB and twice killed the backend with "JavaScript heap out of memory",
  // taking every other company down with it — and because the failure was
  // swallowed below, the operator just saw an empty table with no explanation.
  // We now ask for one page at a time and read the true total from the
  // X-Total-Count header.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    const seq = ++reqSeq.current;
    try {
      const params = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filterLocation) params.location_id = filterLocation;
      if (filterStatus) params.status = filterStatus;
      if (filterCategory) params.category = filterCategory;
      const res = await api.get('/assets', { params });
      // Ignore an answer that a newer request has already superseded —
      // otherwise a slow early query can overwrite a newer, narrower one.
      // `finally` below still clears the spinner on this path.
      if (seq !== reqSeq.current) return;
      setAssets(res.data);
      const t = Number(res.headers?.['x-total-count']);
      setTotal(Number.isFinite(t) ? t : res.data.length);
      setLoadError(null);
    } catch (err) {
      if (seq !== reqSeq.current) return;
      // Never fail silently: an empty table with no message is what made a
      // server crash look like "the data did not import".
      setAssets([]);
      setLoadError(
        err.response?.data?.error ||
        (err.code === 'ECONNABORTED'
          ? 'The server took too long to answer. Please try again.'
          : 'Could not load assets. Please refresh, and tell us if it keeps happening.')
      );
    } finally {
      // In a `finally`, so an early return from a superseded request cannot
      // leave the page spinning for ever.
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [debouncedSearch, filterLocation, filterStatus, filterCategory, page]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);
  // A filter change must restart at page 1; without this you can sit on
  // page 50 of a result set that now has two pages and see nothing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [debouncedSearch, filterLocation, filterStatus, filterCategory]);
  // Auto-refresh on any backend mutation that could affect this list:
  // assets directly, plus locations/categories which we render alongside.
  useChangeStream(() => { fetchAssets(); }, ['assets', 'locations', 'categories']);
  useEffect(() => {
    api.get('/locations').then(r => setLocations(r.data)).catch(() => {});
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    api.get('/assets/statuses').then(r => setStatuses(r.data.statuses)).catch(() => {});
    api.get('/movement-reasons?active=true').then(r => setReasons(r.data)).catch(() => {});
  }, []);

  const handleBulkMove = async () => {
    if (!bulkMove.to_location_id) { showToast('Pick a destination', 'error'); return; }
    try {
      const res = await api.post('/movements/bulk', {
        asset_ids: Array.from(selectedIds),
        to_location_id: Number(bulkMove.to_location_id),
        reason_id: bulkMove.reason_id ? Number(bulkMove.reason_id) : null,
        notes: bulkMove.notes || null,
      });
      const skippedMsg = res.data.skipped.length > 0 ? `, ${res.data.skipped.length} skipped` : '';
      const verb = res.data.status === 'pending' ? 'submitted for approval' : 'moved';
      showToast(`${res.data.created} asset(s) ${verb}${skippedMsg}`,
        res.data.status === 'pending' ? 'warning' : 'success');
      setBulkMoveModal(false);
      setBulkMove({ to_location_id: '', reason_id: '', notes: '' });
      setSelectedIds(new Set());
      fetchAssets();
    } catch (err) {
      showToast(err.response?.data?.error || 'Bulk move failed', 'error');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  // Header-row checkbox: selects every visible row on the CURRENT page only.
  // For "select every filtered match across all pages", use the explicit
  // banner button that appears once a partial selection exists.
  const toggleSelectAll = () => {
    const pageIds = paginated.map(a => a.id);
    const allOnPageSelected = pageIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach(id => next.delete(id));
      else pageIds.forEach(id => next.add(id));
      return next;
    });
  };
  // "Select all matching" can no longer read the ids off the page — only one
  // page is loaded. Ask the server for the matching ids, and refuse to build a
  // selection so large that the bulk actions behind it would time out.
  const SELECT_ALL_CEILING = 5000;
  const selectAllFiltered = async () => {
    if (total > SELECT_ALL_CEILING) {
      showToast(
        `That is ${total.toLocaleString()} assets — too many to act on at once. ` +
        `Narrow it down with the filters (to ${SELECT_ALL_CEILING.toLocaleString()} or fewer) and try again.`,
        'warning'
      );
      return;
    }
    setSelectingAll(true);
    try {
      const params = { ids_only: true, limit: SELECT_ALL_CEILING };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filterLocation) params.location_id = filterLocation;
      if (filterStatus) params.status = filterStatus;
      if (filterCategory) params.category = filterCategory;
      const res = await api.get('/assets', { params });
      setSelectedIds(new Set((res.data || []).map(a => a.id)));
    } catch {
      showToast('Could not select all matching assets. Please try again.', 'error');
    }
    setSelectingAll(false);
  };
  const clearSelection    = () => setSelectedIds(new Set());

  // Label sheet picker (loaded once from /assets/labels/layouts)
  const [labelLayouts, setLabelLayouts] = useState([]);
  const [printModal, setPrintModal] = useState(false);
  const [printOpts, setPrintOpts] = useState({ layout: 'a4-2x5', includeBarcode: true });
  useEffect(() => {
    api.get('/assets/labels/layouts').then(r => setLabelLayouts(r.data)).catch(() => {});
  }, []);

  const handlePrintLabels = () => {
    if (selectedIds.size === 0) {
      showToast('Select one or more assets first', 'warning');
      return;
    }
    setPrintModal(true);
  };

  const confirmPrintLabels = async () => {
    const ids = Array.from(selectedIds);
    try {
      const r = await api.post('/assets/labels',
        { ids, layout: printOpts.layout, includeBarcode: printOpts.includeBarcode },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      setPrintModal(false);
    } catch (err) {
      showToast(err.response?.data?.error || 'Label generation failed', 'error');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadFromApi('/assets/import-template', 'asset_import_template.xlsx');
    } catch (err) {
      showToast(err.message || 'Template download failed', 'error');
    }
  };

  // The server returns exactly one page now, so there is nothing left to slice.
  const paginated = assets;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // A refresh can shrink the result set under you (you bulk-delete the last
  // page, or someone else does), leaving you on an offset past the end with
  // an empty table and no explanation.
  //
  // This MUST stay below `totalPages`. A hook's dependency array is evaluated
  // during render, so when this sat above the declaration it read a const in
  // its temporal dead zone and threw on every render — which took the whole
  // page down to a blank screen (shipped in v2.10.0, fixed in v2.10.1).
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/${deleteModal.id}`);
      showToast('Asset deleted successfully');
      setDeleteModal(null);
      fetchAssets();
    } catch (err) {
      showToast(err.response?.data?.error || 'Delete failed', 'error');
    }
  };

  const handleEditSave = async () => {
    try {
      await api.put(`/assets/${editModal.id}`, editModal);
      showToast('Asset updated successfully');
      setEditModal(null);
      fetchAssets();
    } catch (err) {
      showToast(err.response?.data?.error || 'Update failed', 'error');
    }
  };

  // Two-stage import. The file picker stashes the file into pendingImport,
  // which renders the ConfirmModal asking about duplicates. The actual
  // upload happens only after the user picks a side.
  const handleImport = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || importing) return;
    setPendingImport(file);
  };

  const runImport = async (file, updateExisting) => {
    setPendingImport(null);
    if (!file) return;
    console.log('[import] start', { name: file.name, size: file.size, type: file.type, updateExisting });

    // Upload + progress live in the shared hook; it never throws, it reports
    // an outcome. See lib/useAssetImport.js.
    const outcome = await uploadImport(file, { url: `/assets/import?updateExisting=${updateExisting}` });

    // Refused because an import is already in flight — the button is disabled
    // anyway, so there's nothing to say.
    if (outcome.status === 'busy') return;

    // 422 = all-or-nothing validation failure. NOTHING was imported. The
    // <ImportErrorReport> panel carries the row-by-row detail plus the
    // downloadable workbook, so keep the toast to the headline and make sure
    // no stale success summary is left on screen.
    if (outcome.status === 'invalid') {
      const { failed = 0, total = 0 } = outcome.report || {};
      console.warn('[import] validation failed — nothing imported', outcome.report);
      setImportResult(null);
      showToast(`Import cancelled — ${failed} of ${total} row(s) have errors. Nothing was imported.`, 'error');
      return;
    }

    if (outcome.status === 'error') {
      // Log the full response so 4xx/5xx errors with partial bodies don't
      // disappear into a vacuous "Import failed" toast.
      const err = outcome.error;
      console.error('[import] error', err);
      console.error('[import] error.response?', err.response);
      console.error('[import] error.response?.data?', err.response?.data);
      showToast(`Import failed — ${importErrorMessage(err)}`, 'error');
      return;
    }

    // ── HTTP 200: unchanged summary behaviour ────────────────────────────
    console.log('[import] response', outcome.data);
    const { totalRows = 0, inserted = 0, updated = 0, errorCount = 0, errors = [],
            unfilledFields = [], unmatchedColumns = [] } = outcome.data || {};
    setImportResult(outcome.data);

    // Per-error tally to console so the operator can see WHY rows failed
    // when not all of them landed.
    if (errors.length > 0) {
      const tally = new Map();
      for (const e of errors) {
        const msg = e.errors?.[0] || e.warnings?.[0] || 'unknown';
        tally.set(msg, (tally.get(msg) || 0) + 1);
      }
      const breakdown = [...tally.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([msg, n]) => `  ${n} row(s): ${msg}`)
        .join('\n');
      console.log(`[import] error tally (${errors.length} total):\n${breakdown}`);
    }

    let toastMsg, toastType;
    if (totalRows === 0) {
      toastMsg = 'No rows were imported — the sheet has a header row but no data under it.';
      toastType = 'error';
    } else if (!inserted && !updated && errorCount > 0) {
      const firstErr = errors[0]?.errors?.[0] || errors[0]?.warnings?.[0] || 'unknown';
      toastMsg = `All ${errorCount} row(s) skipped. First error: ${firstErr}. See browser console for the breakdown.`;
      toastType = 'error';
    } else {
      const parts = [];
      if (inserted) parts.push(`${inserted} added`);
      if (updated)  parts.push(`${updated} updated`);
      const summary = parts.length ? parts.join(' · ') : 'no changes';
      toastMsg = `Import complete · ${summary}${errorCount ? ` · ${errorCount} row(s) skipped` : ''}`;
      toastType = errorCount && !inserted && !updated ? 'error' : 'success';
      // Rows can import "successfully" with whole fields empty because a
      // header was renamed. That used to be invisible: bata loaded 145,290
      // rows with their Fixed Assets Number and all three financial columns
      // blank, and only found out weeks later. Say it at import time.
      if (unfilledFields.length) {
        const list = unfilledFields.slice(0, 6).join(', ')
          + (unfilledFields.length > 6 ? ` and ${unfilledFields.length - 6} more` : '');
        toastMsg += ` — but ${unfilledFields.length} field(s) got no data: ${list}.`;
        if (unmatchedColumns.length) {
          const cols = unmatchedColumns.slice(0, 6).join(', ')
            + (unmatchedColumns.length > 6 ? ` and ${unmatchedColumns.length - 6} more` : '');
          toastMsg += ` ${unmatchedColumns.length} column(s) in your file matched nothing: ${cols}. Check the headings match the template.`;
        }
        toastType = 'warning';
      }
    }
    console.log('[import] toast:', toastMsg);
    showToast(toastMsg, toastType);
    fetchAssets();
  };

  // Export what is on screen. The buttons used to export the WHOLE register
  // regardless of the filters, which is both surprising and — past the
  // server's export ceiling — the one request guaranteed to be refused.
  const handleExport = async (type) => {
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (filterLocation) qs.set('location_id', filterLocation);
    if (filterStatus) qs.set('status', filterStatus);
    if (filterCategory) qs.set('category', filterCategory);
    const q = qs.toString();
    try {
      await downloadFromApi(
        `/assets/export/${type}${q ? `?${q}` : ''}`,
        tsFilename(q ? 'assets-filtered' : 'all-assets', type === 'excel' ? 'xlsx' : 'pdf'),
      );
    } catch (err) {
      showToast(err.message || 'Export failed', 'error');
    }
  };

  return (
    <div className="page-container">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Pending bulk-import: ask the operator about duplicates before
          uploading. Replaces the native window.confirm() that some kiosk
          browsers handled inconsistently and that can't be styled. */}
      <ConfirmModal
        open={!!pendingImport}
        title="Import assets"
        message={
          `Importing "${pendingImport?.name || ''}".\n\n` +
          `When a row matches an existing asset by serial number, RFID or QR, what should happen?\n\n` +
          `• Update existing — fill in the columns this file contains, and leave every other ` +
          `field on that asset exactly as it is. A blank cell leaves the current value alone, ` +
          `so a file with only a few columns is safe.\n` +
          `• Skip duplicates — keep existing rows as-is; only insert genuinely new ones.\n\n` +
          `Click X or press Esc to cancel the whole import.`
        }
        confirmLabel="Update existing"
        cancelLabel="Skip duplicates"
        onConfirm={() => runImport(pendingImport, true)}
        onCancel={() => runImport(pendingImport, false)}
        onDismiss={() => setPendingImport(null)}
      />

      {/* Generic confirm modal — used for bulk delete and any other
          destructive action. Set state to null to dismiss. */}
      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel}
        confirmStyle={confirmModal?.confirmStyle}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />

      <div className="page-header">
        <div>
          <div className="page-title"><MdList /> All Assets</div>
          <div className="page-subtitle">
            {total.toLocaleString()} assets {selectedIds.size > 0 && `· ${selectedIds.size} selected`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="btn btn-accent btn-sm"
            onClick={() => setBulkMoveModal(true)}
            disabled={selectedIds.size === 0}
            title={selectedIds.size === 0 ? 'Select one or more rows first' : `Move ${selectedIds.size} asset(s)`}
          >
            <MdSwapHoriz /> Move {selectedIds.size > 0 && `(${selectedIds.size})`}
          </button>
          {can.bulkDelete && (
            <button
              className="btn btn-danger btn-sm"
              onClick={() => {
                if (selectedIds.size === 0) return;
                // Open the styled confirm modal. The actual delete fires
                // from onConfirm — we never block on a synchronous browser
                // dialog (which some kiosk browsers swallow silently).
                setConfirmModal({
                  title: 'Delete selected assets?',
                  message: `You're about to delete ${selectedIds.size} asset(s). This cannot be undone — audit history and movement records for these assets will also be removed.`,
                  confirmLabel: 'Delete',
                  confirmStyle: 'danger',
                  onConfirm: async () => {
                    setConfirmModal(null);
                    try {
                      const r = await api.post('/assets/bulk-delete', { ids: Array.from(selectedIds) });
                      const { deleted = 0, failed = [] } = r.data || {};
                      showToast(
                        `Deleted ${deleted} asset(s)` + (failed.length ? ` · ${failed.length} failed` : ''),
                        failed.length && !deleted ? 'error' : 'success',
                      );
                      setSelectedIds(new Set());
                      fetchAssets();
                    } catch (err) {
                      showToast(err.response?.data?.error || 'Bulk delete failed', 'error');
                    }
                  },
                });
              }}
              disabled={selectedIds.size === 0}
              title={selectedIds.size === 0 ? 'Select one or more rows first' : `Delete ${selectedIds.size} asset(s)`}
            >
              <MdDelete /> Delete {selectedIds.size > 0 && `(${selectedIds.size})`}
            </button>
          )}
          <button
            className="btn btn-primary btn-sm"
            onClick={handlePrintLabels}
            disabled={selectedIds.size === 0}
            title={selectedIds.size === 0 ? 'Select one or more rows first' : `Print ${selectedIds.size} label(s)`}
          >
            <MdPrint /> Print Labels {selectedIds.size > 0 && `(${selectedIds.size})`}
          </button>
          {can.importData && <button className="btn btn-outline btn-sm" onClick={handleDownloadTemplate}><MdDescription /> Template</button>}
          {can.importData && (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => importRef.current.click()}
              disabled={importing}
              title={importing ? 'An import is already running' : 'Import assets from an Excel file'}
            >
              <MdUpload /> {importing ? 'Importing…' : 'Import'}
            </button>
          )}
          <input ref={importRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} disabled={importing} onChange={handleImport} />
          {can.exportExcel && <button className="btn btn-success btn-sm" onClick={() => handleExport('excel')}><MdFileDownload /> Excel</button>}
          {can.exportPdf && <button className="btn btn-danger btn-sm" onClick={() => handleExport('pdf')}><MdPictureAsPdf /> PDF</button>}
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="filters-row" style={{ marginBottom: 0 }}>
          <div className="search-bar" style={{ flex: 2, minWidth: 200 }}>
            <MdSearch />
            <input
              placeholder="Search by name, serial, RFID, barcode..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <MdClose style={{ cursor: 'pointer' }} onClick={() => setSearch('')} />}
          </div>
          <SearchableSelect className="form-control" value={filterLocation} onChange={e => setFilterLocation(e.target.value)} style={{ minWidth: 160 }}>
            <option value="">All Locations</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.location_name}</option>)}
          </SearchableSelect>
          <SearchableSelect className="form-control" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ minWidth: 130 }}>
            <option value="">All Status</option>
            {statuses.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s}</option>)}
          </SearchableSelect>
          <SearchableSelect className="form-control" value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </SearchableSelect>
        </div>
      </div>

      {/* Live upload → processing bar for the Excel import (shared component). */}
      <ImportProgress phase={importPhase} percent={importPercent} fileName={importFileName} />

      {/* All-or-nothing validation failure (HTTP 422): nothing was imported. */}
      <ImportErrorReport report={importErrors} onClose={clearImportErrors} onToast={showToast} />

      {importResult && (
        <div className="card" style={{ marginBottom: 12, borderLeft: `4px solid ${importResult.errorCount > 0 ? '#f57c00' : '#2e7d32'}` }}>
          <div style={{ padding: 12, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <MdInfo style={{ fontSize: 20, color: importResult.errorCount > 0 ? '#f57c00' : '#2e7d32', flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                Import: {importResult.inserted} inserted, {importResult.updated} updated, {importResult.errorCount} error(s) of {importResult.totalRows} row(s)
              </div>
              {importResult.errors?.length > 0 && (
                <details style={{ marginTop: 6 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 12, color: '#777' }}>Show row notes</summary>
                  <div style={{ marginTop: 6, maxHeight: 160, overflowY: 'auto', fontSize: 12, fontFamily: 'monospace', background: '#fff8e1', padding: 8, borderRadius: 4 }}>
                    {importResult.errors.map((e, idx) => {
                      // Backend returns either { errors: [...] } for hard
                      // failures or { warnings: [...] } for soft notices
                      // (e.g. unknown location, still imported). The old
                      // code only handled `errors`, so the first warning row
                      // crashed the render — calling .join() on undefined —
                      // and React unmounted the whole page. Treat both
                      // shapes defensively and surface warnings in amber.
                      const msgs = Array.isArray(e?.errors) ? e.errors
                        : Array.isArray(e?.warnings) ? e.warnings
                        : [String(e?.message ?? e ?? '')];
                      const isWarn = !e?.errors && e?.warnings;
                      return (
                        <div key={idx} style={{ color: isWarn ? '#aa6c00' : 'inherit' }}>
                          {isWarn ? '⚠ ' : '✗ '}Row {e?.row ?? '?'}: {msgs.join('; ')}
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}
            </div>
            <button onClick={() => setImportResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}>×</button>
          </div>
        </div>
      )}

      {/* Gmail-style "select all filtered" banner: appears once you check the
          page header and there are more results past this page. One click then
          extends the selection to every match across every page. */}
      {selectedIds.size > 0 && selectedIds.size < total && (() => {
        const pageIds = paginated.map(a => a.id);
        const allOnPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));
        if (!allOnPageSelected) return null;
        return (
          <div style={{
            background: '#fff8e1', borderLeft: '4px solid #f57c00', padding: '8px 14px',
            marginBottom: 12, borderRadius: 4, fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            <span>
              <strong>{selectedIds.size}</strong> selected on this page.
            </span>
            <button
              className="btn btn-sm btn-outline"
              onClick={selectAllFiltered}
              disabled={selectingAll}
            >
              {selectingAll ? 'Selecting…' : `Select all ${total.toLocaleString()} matching assets`}
            </button>
            <button className="btn btn-sm btn-outline" onClick={clearSelection}>
              Clear selection
            </button>
          </div>
        );
      })()}

      <div className="card">
        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : loadError ? (
          // An empty table with no message is indistinguishable from "the
          // import did not work" — which is exactly how the 145k-asset crash
          // was reported to us. Always say something.
          <div style={{
            padding: 24, textAlign: 'center', background: 'var(--danger-soft, #FDECEC)',
            border: '1px solid var(--danger, #C62828)', borderRadius: 8, color: 'var(--danger, #C62828)',
          }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Couldn't load assets</div>
            <div style={{ fontSize: 13, marginBottom: 12 }}>{loadError}</div>
            <button className="btn btn-sm btn-outline" onClick={fetchAssets}>Try again</button>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <input
                        type="checkbox"
                        checked={paginated.length > 0 && paginated.every(a => selectedIds.has(a.id))}
                        onChange={toggleSelectAll}
                        title="Select all on this page"
                      />
                    </th>
                    <th style={{ width: 28 }}>
                      {/* Global expand-all / collapse-all toggle */}
                      <button
                        className="btn btn-icon btn-sm"
                        title={compactMode ? 'Expand all rows' : 'Collapse all rows'}
                        onClick={() => { setCompactMode(!compactMode); setExpandedIds(new Set()); }}
                        style={{ background: 'transparent', border: 'none', padding: 0 }}
                      >
                        {compactMode ? <MdUnfoldMore /> : <MdUnfoldLess />}
                      </button>
                    </th>
                    <th>#</th>
                    <th>Asset Name</th>
                    <th>Location</th>
                    <th>Status</th>
                    {!compactMode && <th>Asset Tag</th>}
                    {!compactMode && <th>Serial #</th>}
                    {!compactMode && <th>RFID Tag</th>}
                    {!compactMode && <th>Identifiers</th>}
                    {!compactMode && <th>Category</th>}
                    {!compactMode && <th>Registered</th>}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 ? (
                    <tr><td colSpan={13} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No assets found</td></tr>
                  ) : paginated.map((asset, i) => {
                    const expanded = isRowExpanded(asset.id);
                    return (
                      <React.Fragment key={asset.id}>
                        <tr style={{ cursor: 'pointer' }}>
                          <td onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(asset.id)}
                              onChange={() => toggleSelect(asset.id)}
                            />
                          </td>
                          <td onClick={e => { e.stopPropagation(); toggleRow(asset.id); }} style={{ textAlign: 'center', cursor: 'pointer' }}
                              title={expanded ? 'Collapse this row' : 'Expand this row'}>
                            <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>
                              {expanded ? '▾' : '▸'}
                            </span>
                          </td>
                          <td onClick={() => nav(`/assets/${asset.id}`)} style={{ color: 'var(--text-muted)', fontSize: 12 }}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                          <td onClick={() => nav(`/assets/${asset.id}`)} style={{ fontWeight: 500, color: 'var(--accent)' }}>{asset.asset_name}</td>
                          <td onClick={() => nav(`/assets/${asset.id}`)}>{asset.location_name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                          <td onClick={() => nav(`/assets/${asset.id}`)}><span className={`badge status-${asset.status}`}>{asset.status}</span></td>
                          {!compactMode && <td onClick={() => nav(`/assets/${asset.id}`)}><code style={{ fontSize: 11 }}>{asset.asset_tag}</code></td>}
                          {!compactMode && <td onClick={() => nav(`/assets/${asset.id}`)}><code style={{ fontSize: 11 }}>{asset.serial_number}</code></td>}
                          {!compactMode && <td onClick={() => nav(`/assets/${asset.id}`)}><code style={{ fontSize: 11, color: 'var(--accent)' }}>{asset.rfid_tag}</code></td>}
                          {!compactMode && <td onClick={() => nav(`/assets/${asset.id}`)}><IdentifierBadges asset={asset} /></td>}
                          {!compactMode && <td onClick={() => nav(`/assets/${asset.id}`)}><span className="badge badge-info">{asset.category || '—'}</span></td>}
                          {!compactMode && <td onClick={() => nav(`/assets/${asset.id}`)} style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{new Date(asset.created_at).toLocaleDateString()}</td>}
                          <td onClick={e => e.stopPropagation()}>
                            <div className="action-buttons">
                              <button className="btn btn-accent btn-icon btn-sm" title="Edit"
                                onClick={() => setEditModal({ ...asset })}>
                                <MdEdit />
                              </button>
                              <button className="btn btn-danger btn-icon btn-sm" title="Delete"
                                onClick={() => setDeleteModal(asset)}>
                                <MdDelete />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {/* Detail strip — only renders for rows the user has
                            expanded (or for everything when not in compactMode
                            with the row toggled away). The strip lives in its
                            own <tr> with a single colSpan so it slots neatly
                            under the row without breaking column alignment. */}
                        {compactMode && expanded && (
                          <tr>
                            <td colSpan={7} style={{ background: '#fafbfc', padding: '10px 14px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, fontSize: 12 }}>
                                <div><strong>Asset Tag:</strong> <code>{asset.asset_tag || '—'}</code></div>
                                <div><strong>Serial #:</strong> <code>{asset.serial_number || '—'}</code></div>
                                <div><strong>RFID:</strong> <code style={{ color: 'var(--accent)' }}>{asset.rfid_tag || '—'}</code></div>
                                <div><strong>Identifiers:</strong> <IdentifierBadges asset={asset} /></div>
                                <div><strong>Category:</strong> {asset.category || '—'}</div>
                                <div><strong>Registered:</strong> {asset.created_at ? new Date(asset.created_at).toLocaleDateString() : '—'}</div>
                                {asset.barcode && <div><strong>Barcode:</strong> <code>{asset.barcode}</code></div>}
                                {asset.nfc_tag && <div><strong>NFC:</strong> <code>{asset.nfc_tag}</code></div>}
                                {asset.qr_code && <div><strong>QR:</strong> <code style={{ wordBreak: 'break-all' }}>{asset.qr_code}</code></div>}
                                {asset.notes && <div style={{ gridColumn: '1 / -1' }}><strong>Notes:</strong> {asset.notes}</div>}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <div className="pagination-info">
                  Showing {((page - 1) * PAGE_SIZE + 1).toLocaleString()}–{Math.min(page * PAGE_SIZE, total).toLocaleString()} of {total.toLocaleString()}
                </div>
                <div className="pagination-controls" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                  <button className="page-btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                  <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
                  {compactPagerWindow(page, totalPages).map((p, i) =>
                    p === '…' ? (
                      <span key={`gap-${i}`} style={{ padding: '0 6px', color: 'var(--text-muted)' }}>…</span>
                    ) : (
                      <button
                        key={p}
                        className={`page-btn ${page === p ? 'active' : ''}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
                  <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
                  <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                    Page {page} of {totalPages}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><MdEdit /> Edit Asset</div>
              <button className="btn btn-icon" onClick={() => setEditModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Asset Name</label>
                  <input className="form-control" value={editModal.asset_name}
                    onChange={e => setEditModal(m => ({ ...m, asset_name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Asset Tag</label>
                  <input className="form-control" value={editModal.asset_tag || ''}
                    onChange={e => setEditModal(m => ({ ...m, asset_tag: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Serial Number</label>
                  <input className="form-control" value={editModal.serial_number}
                    onChange={e => setEditModal(m => ({ ...m, serial_number: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">RFID Tag</label>
                  <input className="form-control" value={editModal.rfid_tag}
                    onChange={e => setEditModal(m => ({ ...m, rfid_tag: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <SearchableSelect className="form-control" value={editModal.category || ''}
                    onChange={e => setEditModal(m => ({ ...m, category: e.target.value }))}>
                    <option value="">— None —</option>
                    {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </SearchableSelect>
                </div>
                <div className="form-group">
                  <label className="form-label">Location</label>
                  <SearchableSelect className="form-control" value={editModal.location_id || ''}
                    onChange={e => setEditModal(m => ({ ...m, location_id: e.target.value }))}>
                    <option value="">Unassigned</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.location_name}</option>)}
                  </SearchableSelect>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <SearchableSelect className="form-control" value={editModal.status}
                    onChange={e => setEditModal(m => ({ ...m, status: e.target.value }))}>
                    {statuses.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s}</option>)}
                  </SearchableSelect>
                </div>
                <div className="form-group">
                  <label className="form-label">Barcode</label>
                  <input className="form-control" value={editModal.barcode || ''}
                    onChange={e => setEditModal(m => ({ ...m, barcode: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">QR Code</label>
                  <input className="form-control" value={editModal.qr_code || ''}
                    onChange={e => setEditModal(m => ({ ...m, qr_code: e.target.value }))}
                    placeholder="QR payload" />
                </div>
                <div className="form-group">
                  <label className="form-label">NFC Tag UID</label>
                  <input className="form-control" value={editModal.nfc_tag || ''}
                    onChange={e => setEditModal(m => ({ ...m, nfc_tag: e.target.value }))}
                    placeholder="NFC UID" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" value={editModal.notes || ''}
                  onChange={e => setEditModal(m => ({ ...m, notes: e.target.value }))} rows={2} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEditSave}><MdSave /> Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Move Modal */}
      {/* Print labels — sheet picker */}
      {printModal && (
        <div className="modal-overlay" onClick={() => setPrintModal(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><MdPrint /> Print {selectedIds.size} label(s)</div>
              <button className="btn btn-icon" onClick={() => setPrintModal(false)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Sheet layout</label>
                <SearchableSelect className="form-control" value={printOpts.layout}
                  onChange={e => setPrintOpts(o => ({ ...o, layout: e.target.value }))}>
                  {labelLayouts.map(l => (
                    <option key={l.key} value={l.key}>
                      {l.label} — {l.size} {l.cols}×{l.rows}
                    </option>
                  ))}
                </SearchableSelect>
                <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                  Pick the layout that matches your label sheet stock.
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={printOpts.includeBarcode}
                  onChange={e => setPrintOpts(o => ({ ...o, includeBarcode: e.target.checked }))} />
                Include Code-128 barcode (in addition to QR)
              </label>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setPrintModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmPrintLabels}>
                <MdPrint /> Generate PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkMoveModal && (
        <div className="modal-overlay" onClick={() => setBulkMoveModal(false)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title"><MdSwapHoriz /> Move {selectedIds.size} asset(s)</div>
              <button className="btn btn-icon" onClick={() => setBulkMoveModal(false)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Destination *</label>
                <SearchableSelect className="form-control" value={bulkMove.to_location_id}
                  onChange={e => setBulkMove(b => ({ ...b, to_location_id: e.target.value }))}>
                  <option value="">— Select location —</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.location_code} · {l.location_name}</option>)}
                </SearchableSelect>
              </div>
              <div className="form-group">
                <label className="form-label">Reason</label>
                <SearchableSelect className="form-control" value={bulkMove.reason_id}
                  onChange={e => setBulkMove(b => ({ ...b, reason_id: e.target.value }))}>
                  <option value="">— None —</option>
                  {reasons.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.name}{r.requires_approval ? ' (requires approval)' : ''}
                    </option>
                  ))}
                </SearchableSelect>
                {bulkMove.reason_id && reasons.find(r => r.id === Number(bulkMove.reason_id))?.requires_approval && (
                  <div style={{ fontSize: 11, color: '#ef6c00', marginTop: 4 }}>
                    ⚠ This reason requires approval. Movements will be submitted as pending.
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={bulkMove.notes}
                  onChange={e => setBulkMove(b => ({ ...b, notes: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setBulkMoveModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleBulkMove}>
                <MdSwapHoriz /> Move Assets
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title" style={{ color: 'var(--danger)' }}>⚠ Delete Asset</div>
              <button className="btn btn-icon" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p className="confirm-msg">
                Are you sure you want to delete <span className="confirm-value">"{deleteModal.asset_name}"</span>?
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}><MdDelete /> Delete</button>
            </div>
          </div>
        </div>
      )}
      {/* Quick-add FAB — jump straight to Asset Registration. Hidden when the
          user lacks the asset-registration permission. */}
      {can.create && (
        <button className="fab" title="Register a new asset" onClick={() => nav('/asset-registration')}>
          <MdAdd />
        </button>
      )}
    </div>
  );
};

export default AllAssets;
